import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    title: { type: String, default: "Unsaved Changes" },
    message: { type: String, default: "You have unsaved changes that will be lost. Are you sure you want to leave this page?" },
    confirmText: { type: String, default: "Leave Page" },
    cancelText: { type: String, default: "Stay on Page" }
  }

  connect() {
    this.isDirty = false
    this.pendingNavigation = null
    this.formId = this.element.id
    this.initialFormData = this.captureFormData()

    this.boundHandleChange = this.handleChange.bind(this)
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)
    this.boundHandleTurboBeforeVisit = this.handleTurboBeforeVisit.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleDrawerBeforeClose = this.handleDrawerBeforeClose.bind(this)

    // Listen on document to catch changes from inputs with form="formId" attribute
    document.addEventListener("input", this.boundHandleChange)
    document.addEventListener("change", this.boundHandleChange)
    window.addEventListener("beforeunload", this.boundHandleBeforeUnload)
    document.addEventListener("turbo:before-visit", this.boundHandleTurboBeforeVisit)
    document.addEventListener("admin-drawer:before-close", this.boundHandleDrawerBeforeClose)

    this.createModal()
  }

  disconnect() {
    document.removeEventListener("input", this.boundHandleChange)
    document.removeEventListener("change", this.boundHandleChange)
    window.removeEventListener("beforeunload", this.boundHandleBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundHandleTurboBeforeVisit)
    document.removeEventListener("admin-drawer:before-close", this.boundHandleDrawerBeforeClose)
    this.removeModal()
  }

  handleChange(event) {
    // Check if the changed element belongs to this form
    const target = event.target
    if (!this.isFormElement(target)) return

    const currentFormData = this.captureFormData()
    this.isDirty = currentFormData !== this.initialFormData
  }

  isFormElement(element) {
    // Element is inside the form
    if (this.element.contains(element)) return true
    // Element has form attribute pointing to this form
    if (element.form === this.element) return true
    if (element.getAttribute("form") === this.formId) return true
    return false
  }

  handleBeforeUnload(event) {
    if (this.isDirty) {
      event.preventDefault()
      event.returnValue = this.messageValue
      return this.messageValue
    }
  }

  handleTurboBeforeVisit(event) {
    if (this.isDirty) {
      event.preventDefault()
      this.pendingNavigation = event.detail.url
      this.showModal()
    }
  }

  handleDrawerBeforeClose(event) {
    if (this.isDirty) {
      event.preventDefault()
      this.pendingDrawerClose = true
      this.showModal()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.cancelNavigation()
    }
  }

  allowSubmit() {
    this.isDirty = false
  }

  captureFormData() {
    const formData = new FormData(this.element)
    const entries = []
    for (const [key, value] of formData.entries()) {
      entries.push(`${key}=${value}`)
    }
    return entries.sort().join("&")
  }

  createModal() {
    this.modal = document.createElement("div")
    this.modal.id = "unsaved-changes-modal"
    this.modal.className = "fixed inset-0 z-50 hidden items-center justify-center"
    this.modal.innerHTML = `
      <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" id="unsaved-changes-backdrop"></div>
      <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 transform transition-all">
        <div class="flex items-center gap-3 mb-4">
          <div class="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-[#062B4B]">${this.titleValue}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-6">${this.messageValue}</p>
        <div class="flex gap-3 justify-end">
          <button type="button" id="unsaved-changes-cancel"
                  class="px-4 py-2 text-sm font-medium text-[#084572] bg-white border border-[#084572]/30 rounded-full hover:bg-gray-50 transition cursor-pointer">
            ${this.cancelTextValue}
          </button>
          <button type="button" id="unsaved-changes-confirm"
                  class="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-full transition cursor-pointer">
            ${this.confirmTextValue}
          </button>
        </div>
      </div>
    `
    document.body.appendChild(this.modal)

    // Attach event listeners directly
    this.modal.querySelector("#unsaved-changes-backdrop").addEventListener("click", () => this.cancelNavigation())
    this.modal.querySelector("#unsaved-changes-cancel").addEventListener("click", () => this.cancelNavigation())
    this.modal.querySelector("#unsaved-changes-confirm").addEventListener("click", () => this.confirmNavigation())
  }

  removeModal() {
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal)
    }
  }

  showModal() {
    this.modal.classList.remove("hidden")
    this.modal.classList.add("flex")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.boundHandleKeydown)
    // Focus the cancel button for accessibility
    this.modal.querySelector("#unsaved-changes-cancel").focus()
  }

  hideModal() {
    this.modal.classList.add("hidden")
    this.modal.classList.remove("flex")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  confirmNavigation() {
    this.isDirty = false
    this.hideModal()
    if (this.pendingDrawerClose) {
      this.pendingDrawerClose = false
      document.dispatchEvent(new CustomEvent("admin-drawer:force-close"))
    } else if (this.pendingNavigation) {
      window.location.href = this.pendingNavigation
      this.pendingNavigation = null
    }
  }

  cancelNavigation() {
    this.pendingNavigation = null
    this.hideModal()
  }
}
